/* HermesLoader — lightweight route-change transition.

   Sequence (~600ms):
     0-300ms  tiny serif wordmark fade-in (center)
     200-400ms gold hairline rule sweeps left-to-right across the wordmark
     350-500ms content slides up 12px + fades in

   - Fires on major route boundaries only:
       * inside Lumetier marketing (`/lumetier`, `/marketing`, `/marketing/*`)
       * Allure surfaces (any non-admin route)
       * Admin shell — but only between section boundaries (e.g. `/admin/galleries`
         → `/admin/bookings`), not within-section navigation (tab changes etc.)
   - Skipped if a MaisonIntro is currently playing for this surface
     (we check the `maison_intro_shown_*` flag — once set, intro is done).
   - `prefers-reduced-motion`: instant nav, no overlay.
*/
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function adminSection(path) {
  // /admin/galleries/abc/edit → 'galleries'. Anything past 3 segments is
  // considered "within-section" (tabs, nested views) and should NOT trigger.
  const parts = path.split("/").filter(Boolean);
  if (parts[0] !== "admin") return null;
  return parts[1] || null;
}

function lumetierSurfaceSection(path) {
  // Returns a non-null section token for ANY Lumetier-branded surface.
  // Lumetier product surfaces live under multiple prefixes:
  //   `/lumetier`              → marketing landing (allure-app routing)
  //   `/lumetier/galleries`,
  //   `/lumetier/website`,
  //   `/lumetier/manager`,
  //   `/lumetier/store`,
  //   `/lumetier/onboard`      → product pages + onboarding
  //   `/lumetier-preview`      → studio-preview iframe surface
  //   `/marketing`,
  //   `/marketing/*`           → legacy marketing landing route
  // Hash navigation (`/lumetier#features`) stays within the same section,
  // so the loader does not double-fire.
  if (path === "/lumetier" || path === "/lumetier-preview") return "hub";
  if (path === "/marketing") return "marketing";
  if (path.startsWith("/lumetier/") || path.startsWith("/marketing/")) {
    // Strip query/hash; keep the path itself as the section identifier so
    // navigating between two product pages (`/lumetier/galleries`
    // → `/lumetier/website`) registers as a section change.
    return path.replace(/[?#].*$/, "");
  }
  return null;
}

function tenantSlug(path) {
  // Returns the tenant slug for any `/s/<slug>/...` path, else null.
  // We strip query/hash before matching so navigation that only differs
  // in `?service=` (e.g. booking deep-links) doesn't count as a new tenant.
  const m = path.replace(/[?#].*$/, "").match(/^\/s\/([^/]+)/);
  return m ? m[1] : null;
}

function slugToLabel(slug) {
  // 'skenzy-studio' → 'SKENZY STUDIO'. Best-effort: when the tenant has
  // not been seen this session we don't have their settings hydrated, so
  // we derive a readable wordmark from the slug rather than flashing
  // the platform default ('ALLURE IMAGE') and confusing visitors.
  if (!slug) return "";
  return slug.replace(/[-_]+/g, " ").trim().toUpperCase();
}

function detectSurfaceTransition(prev, next) {
  // Returns the wordmark to display ("LUMETIER" / "<TENANT NAME>")
  // or null if the loader should NOT fire for this transition.
  if (prev === next) return null;

  const prevAdminSec = adminSection(prev);
  const nextAdminSec = adminSection(next);
  // Admin shell is the platform chrome — always wear the LUMETIER mark.
  if (prevAdminSec && nextAdminSec) {
    if (prevAdminSec === nextAdminSec) return null; // same section, tab swap
    return "LUMETIER";
  }
  // Crossing between admin ↔ public: yes, fire with platform mark.
  if (prevAdminSec || nextAdminSec) return "LUMETIER";

  const prevLM = lumetierSurfaceSection(prev);
  const nextLM = lumetierSurfaceSection(next);
  if (nextLM || prevLM) return "LUMETIER";

  // Tenant public surfaces (`/s/<slug>/...`) — show the TENANT's
  // wordmark, not Allure's. Without this branch the loader flashed
  // "ALLURE IMAGE" mid-navigation on every other studio's site (the
  // hardcoded default at the bottom of this function), which read as
  // a brand leak to visitors on Skenzy / Target / Fola Fola / etc.
  // Same-tenant nav (Portfolio → Services on the same studio) still
  // counts as a section change so the loader fires once per page, but
  // crossing BETWEEN tenants would be unusual — that returns the
  // destination tenant's wordmark.
  const prevTenant = tenantSlug(prev);
  const nextTenant = tenantSlug(next);
  if (nextTenant) return slugToLabel(nextTenant);
  if (prevTenant) return slugToLabel(prevTenant);

  // Allure root surfaces (no slug, no admin, no Lumetier prefix). The
  // root tenant of the platform is Allure Image itself, so the wordmark
  // here is correct.
  return "ALLURE IMAGE";
}

function introInProgress() {
  // Check if any MaisonIntro is mid-playback. We use sessionStorage flags
  // set when an intro starts; absent flag = not played; if intro has set
  // the flag we are AFTER it played and Hermès can fire.
  if (typeof window === "undefined") return false;
  // No flag (first visit) AND on a surface that mounts an intro → block.
  // We're conservative: if user hasn't seen ANY intro this session AND the
  // path matches a surface that would mount it, suppress this loader for
  // the first nav so the intro plays cleanly.
  const path = window.location.pathname;
  if (path === "/lumetier" && !sessionStorage.getItem("maison_intro_shown_lumetier")) return true;
  if (path === "/" && !sessionStorage.getItem("maison_intro_shown_allure")) return true;
  return false;
}

export default function HermesLoader() {
  const location = useLocation();
  const prevPath = useRef(location.pathname);
  const [visible, setVisible] = useState(false);
  const [label, setLabel] = useState("LUMETIER");

  useEffect(() => {
    const next = location.pathname;
    const prev = prevPath.current;
    prevPath.current = next;

    if (prefersReducedMotion()) return;
    if (introInProgress()) return;

    const surface = detectSurfaceTransition(prev, next);
    if (!surface) return;

    setLabel(surface);
    setVisible(true);
    const t1 = setTimeout(() => setVisible(false), 600);
    return () => clearTimeout(t1);
  }, [location.pathname]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="hermes-loader"
          aria-hidden="true"
          data-testid="hermes-loader"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          className="fixed inset-0 z-[9985] pointer-events-none flex items-center justify-center"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(14,13,12,0.78) 0%, rgba(14,13,12,0.92) 60%, rgba(14,13,12,0.96) 100%)",
            backdropFilter: "blur(2px)",
          }}
        >
          <div className="flex flex-col items-center gap-3">
            <motion.span
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="text-[14px] tracking-[0.46em] font-light"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                color: "#F5EFE0",
              }}
            >
              {label}
            </motion.span>
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.32, delay: 0.15, ease: [0.83, 0, 0.17, 1] }}
              style={{
                background: "linear-gradient(90deg, transparent 0%, #C9A961 50%, transparent 100%)",
                width: 120, height: 1,
                transformOrigin: "left center",
              }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
